import { Avatar, Box, Stack, Typography, useTheme } from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Security } from '../../../../proto/generated/security'
import { LOGOS_BASE_URL } from '../../../../assets/constants'
import { getColorForSymbol } from '../../../../util/general'
import { getSecurityKey } from '../../../../util/security'
import OptionsExpiryMenuBody from '../../../../pages/home/optionsChartHeader/optionsChartExpiryMenu/optionsExpiryMenuBody'
import { selectUnderlyingOptionsExpiriesV2, selectUnderlyingOptionsTradeData } from '../../../../selectors/options'
import { DropDownExpiries } from '../../../../reducer/options.reducer'
import { optionsTradesDataType } from '../../../../pages/typings/options.typings'
import SeriesTitleButton from '../../../chartV2/seriesMetaDisplay/SeriesTitleButton'
import { setSecuritySearchOpen } from '../../../../reducer/home.reducer'
import { OptionChainViewKey } from '../../../../assets/constants'
import OptionChainTimeIntervalMenuBody from '../../../optionChain/header/optionChainTimeIntervalMenuBody'

type Props = {
    security: Security
    expandedRows: string[]
    onExpiryChange: (index: number) => void
    onSecuritySelected?: (key: string, expressionMode: boolean, securities: string[]) => void
    optionChainViewKey?: OptionChainViewKey
}

const OptionChainWidgetMetadata = ({
    security,
    expandedRows,
    onExpiryChange,
    onSecuritySelected,
    optionChainViewKey = OptionChainViewKey.WIDGET,
}: Props) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const underlyingToken = useMemo(
        () => getSecurityKey(security.exchange, security.code),
        [security.exchange, security.code]
    )
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const dropdownExpiries = useSelector(selectUnderlyingOptionsExpiriesV2(underlyingToken, true))

    const symbolText = security.symbol ?? security.code ?? ''

    const selectedExpiryName = useMemo(() => {
        const idx = Number(expandedRows?.[0] ?? 0)
        const tradeRow = optionExpiries?.[idx]
        if (!tradeRow || tradeRow.derived) {
            return dropdownExpiries?.[0]?.expiry?.name ?? ''
        }
        const match = dropdownExpiries?.find(
            (o) => o.expiry.expiry === tradeRow.expiration.expiry?.expiry
        )
        return match?.expiry.name ?? tradeRow.expiration?.name ?? ''
    }, [expandedRows, optionExpiries, dropdownExpiries])

    const handleExpirySelect = useCallback(
        (dropdownExpiry: DropDownExpiries) => {
            const idx =
                optionExpiries?.findIndex(
                    (row: optionsTradesDataType) =>
                        !row.derived &&
                        row.expiration.expiry?.expiry === dropdownExpiry.expiry.expiry
                ) ?? -1
            onExpiryChange(idx >= 0 ? idx : 0)
        },
        [optionExpiries, onExpiryChange]
    )

    const logo = useMemo(() => {
        const symbol = security.symbol || security.code || ''
        if (security.logoUrl) {
            return (
                <Avatar
                    alt={symbolText}
                    src={`${LOGOS_BASE_URL}${security.logoUrl}.svg`}
                    sx={{ width: 18, height: 18, flexShrink: 0 }}
                />
            )
        }
        return (
            <Avatar
                alt={symbolText}
                sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    fontSize: 10,
                    color: 'white',
                    bgcolor: getColorForSymbol(symbol, theme.palette.background.default)
                }}
            >
                {symbol
                    .split(' ')
                    .map((word) => word[0]?.toUpperCase())
                    .slice(0, 2)
                    .join('')}
            </Avatar>
        )
    }, [security.logoUrl, symbolText, security.symbol, security.code, theme.palette.background.default])

    const onTitleClick = useCallback(() => {
        const secKey = getSecurityKey(security.exchange, security.code)
        dispatch(
            setSecuritySearchOpen({
                open: true,
                key: secKey,
                isExpression: false,
                onSecuritySelected,
            })
        )
    }, [dispatch, onSecuritySelected, security])

    return (
        <Box
            className="pane-meta-container font-feature-num"
            sx={{
                pointerEvents: 'all',
                zIndex: 4,
                maxWidth: 'calc(100% - 8px)'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                    gap: 0.25,
                    minWidth: 0,
                    maxWidth: '100%',
                    height: '24px',
                }}
            >
                <SeriesTitleButton onClick={onTitleClick}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {logo}
                        <Typography
                            variant="body2"
                            sx={{
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: theme.palette.text.primary,
                            }}
                        >
                            {symbolText}
                        </Typography>
                    </Stack>
                </SeriesTitleButton>
                {selectedExpiryName ? (
                    <OptionsExpiryMenuBody
                        underlyingToken={underlyingToken}
                        expiryLabel={selectedExpiryName}
                        onSelect={handleExpirySelect}
                        hidePastExpiry
                        hideDerivedInMenu
                        excludeDerivedDropdown
                    />
                ) : null}
                <OptionChainTimeIntervalMenuBody optionChainViewKey={optionChainViewKey} />
            </Box>
        </Box>
    )
}

export default memo(OptionChainWidgetMetadata)
